<template>
    <div class="au" v-if="fileName || !isUser">
        <div :class="['au__zone', { 'au__zone--active': isActive, 'au__zone--drag': isDragging }]"
            @click="clickFile"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @drop.prevent="handleDrop"
        >
            <!-- Has file -->
            <template v-if="isActive">
                <!-- Image preview -->
                <img v-if="previewUrl && isImage" :src="previewUrl" class="au__preview" />
                <!-- File icon -->
                <div v-else class="au__file-icon">
                    <i :class="fileIcon"></i>
                </div>
                <div class="au__info">
                    <span class="au__name" :title="fileName">{{ truncatedName }}</span>
                    <span class="au__size" v-if="fileSize">{{ fileSize }}</span>
                </div>
            </template>

            <!-- Empty state -->
            <template v-else>
                <div class="au__add-icon">
                    <i class="fas fa-cloud-upload-alt"></i>
                </div>
                <span class="au__add-text">Upload File {{ id }}</span>
                <span class="au__add-hint">Click or drag &amp; drop</span>
            </template>
        </div>

        <!-- Remove button -->
        <button v-if="isActive && !readonly && !isUser" class="au__remove" @click.stop="removeFile" title="Remove file">
            <i class="fas fa-times"></i>
        </button>

        <!-- Open button -->
        <button v-if="isActive && name" class="au__open" @click.stop="openFile" title="Open file">
            <i class="fas fa-external-link-alt"></i>
        </button>

        <input style="display:none" type="file" @input="handleInput" :ref="'fileInput' + id" />
    </div>
</template>

<script>
import { serverURL } from "@/utils/http"

function filterFileName(name) {
    return name.replaceAll("/uploads/", "").replaceAll("/uploads", "")
}

function formatSize(bytes) {
    if (!bytes) return ""
    if (bytes < 1024) return bytes + " B"
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB"
    return (bytes / 1048576).toFixed(1) + " MB"
}

const IMAGE_EXT = ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"]

export default {
    model: {
        prop: "value",
        event: "input",
    },
    emits: ["input", "remove"],
    props: {
        name: { type: String, default: "" },
        id: { type: [String, Number], default: 1 },
        value: { type: [File, Object, String], default: () => null },
        readonly: { type: Boolean, default: false },
        isUser: { type: Boolean, default: false },
    },
    data() {
        return {
            fileName: "",
            file: null,
            isDragging: false,
            previewUrl: "",
            rawSize: 0,
        }
    },
    mounted() {
        if (this.name) {
            this.fileName = filterFileName(this.name)
            if (this.isImage) {
                this.previewUrl = serverURL + this.name
            }
        }
    },
    computed: {
        isActive() {
            return !!this.fileName
        },
        truncatedName() {
            if (!this.fileName) return ""
            if (this.fileName.length <= 22) return this.fileName
            const ext = this.fileName.split(".").pop()
            return this.fileName.substring(0, 16) + "…." + ext
        },
        fileSize() {
            return formatSize(this.rawSize)
        },
        fileExt() {
            return (this.fileName || "").split(".").pop().toLowerCase()
        },
        isImage() {
            return IMAGE_EXT.includes(this.fileExt)
        },
        fileIcon() {
            const ext = this.fileExt
            if (IMAGE_EXT.includes(ext)) return "fas fa-image"
            if (["pdf"].includes(ext)) return "fas fa-file-pdf"
            if (["doc", "docx"].includes(ext)) return "fas fa-file-word"
            if (["xls", "xlsx", "csv"].includes(ext)) return "fas fa-file-excel"
            if (["zip", "rar", "7z"].includes(ext)) return "fas fa-file-archive"
            if (["mp4", "mov", "avi", "mkv"].includes(ext)) return "fas fa-file-video"
            return "fas fa-file-alt"
        },
    },
    methods: {
        handleInput(e) {
            if (e.target.files[0]) {
                this.setFile(e.target.files[0])
            }
        },
        handleDrop(e) {
            this.isDragging = false
            const files = e.dataTransfer.files
            if (files && files[0]) {
                this.setFile(files[0])
            }
        },
        setFile(file) {
            this.file = file
            this.fileName = file.name
            this.rawSize = file.size
            this.$emit("input", this.file)
            // Generate preview for images
            if (this.isImage) {
                const reader = new FileReader()
                reader.onload = (e) => { this.previewUrl = e.target.result }
                reader.readAsDataURL(file)
            } else {
                this.previewUrl = ""
            }
        },
        removeFile() {
            this.file = null
            this.fileName = ""
            this.previewUrl = ""
            this.rawSize = 0
            this.$emit("input", this.file)
            if (this.name) {
                this.$emit("remove", this.name)
            }
        },
        clickFile() {
            if (!this.isActive) {
                this.$refs["fileInput" + this.id].click()
            } else if (!this.name) {
                // Re-select file
                this.$refs["fileInput" + this.id].click()
            }
        },
        openFile() {
            if (this.name) {
                window.open(serverURL + this.name)
            }
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.au {
    position: relative;
    display: inline-block;
}

.au__zone {
    width: 120px;
    min-height: 110px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    border: 2px dashed $gray200;
    border-radius: 10px;
    background: $gray50;
    cursor: pointer;
    transition: all 0.2s ease;
    padding: 10px 8px;
    text-align: center;

    &:hover {
        border-color: $primaryColor;
        background: rgba($primaryColor, 0.03);
    }

    &--active {
        border-style: solid;
        border-color: $gray200;
        background: white;
        &:hover { border-color: $gray300; background: $gray50; }
    }

    &--drag {
        border-color: $primaryColor;
        background: rgba($primaryColor, 0.06);
        transform: scale(1.02);
    }
}

/* ── Empty state ── */
.au__add-icon {
    width: 32px; height: 32px; border-radius: 50%;
    background: $primaryLight; color: $primaryColor;
    display: flex; align-items: center; justify-content: center;
    font-size: 14px; margin-bottom: 2px;
}
.au__add-text {
    font-size: 11px; font-weight: 600; color: $gray600;
}
.au__add-hint {
    font-size: 9.5px; color: $gray400; font-weight: 500;
}

/* ── File preview ── */
.au__preview {
    width: 100%; height: 56px;
    object-fit: cover; border-radius: 6px;
    margin-bottom: 2px;
}
.au__file-icon {
    width: 40px; height: 40px; border-radius: 8px;
    background: $gray100; color: $gray500;
    display: flex; align-items: center; justify-content: center;
    font-size: 16px; margin-bottom: 2px;
}
.au__info {
    display: flex; flex-direction: column; align-items: center; gap: 1px;
    width: 100%; padding: 0 2px;
}
.au__name {
    font-size: 10.5px; font-weight: 600; color: $gray700;
    max-width: 100%; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.au__size {
    font-size: 9.5px; color: $gray400; font-weight: 500;
}

/* ── Action buttons ── */
.au__remove {
    position: absolute; top: -6px; right: -6px;
    width: 20px; height: 20px; border-radius: 50%;
    background: white; border: 1px solid $gray200;
    color: $gray400; font-size: 9px;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; transition: all 0.15s; z-index: 2; padding: 0;
    &:hover { background: rgba(#ef4444, .1); border-color: #ef4444; color: #ef4444; }
}
.au__open {
    position: absolute; top: -6px; right: 18px;
    width: 20px; height: 20px; border-radius: 50%;
    background: white; border: 1px solid $gray200;
    color: $gray400; font-size: 8px;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; transition: all 0.15s; z-index: 2; padding: 0;
    &:hover { background: rgba($primaryColor, .1); border-color: $primaryColor; color: $primaryColor; }
}
</style>
